import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { ErrorBoundary } from 'wsm-error-boundary';
import { ButtonList, ButtonListTwoColumn } from 'wsm-vehicle-cta-display';
import { InstantEpriceContainer } from 'wsm-pricing-display';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import { usePrefs, useFlags } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';

import { pricingMap } from '../../../utilities/pricingMap';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import { getVehicleObj } from '../../../utilities/vehicleTracking';
import { validateWindowStickerCondition } from '../../../utilities/validateWindowStickerCondition';
import WindowSticker from './WindowSticker';

const CTAButtons = ({
	accountId,
	isDisplayingEPrice,
	setIsDisplayingEPrice,
	vehicle
}) => {
	const {
		accountId: vehicleAccountId,
		uuid,
		inventoryButtons = [],
		pricing = {}
	} = vehicle;
	const userProfile = useSelector((state) => state.userProfile);
	const pageAlias = useSelector((state) => state.widgetInfo.pageAlias);
	const { franchiseList } = useSelector((state) => state.franchiseList);
	const { accounts } = useSelector((state) => state.accountData);

	const flags = useFlags();
	const {
		buttonSizeEPrice,
		buttonStyleEPrice,
		dealCentralRemoveCTAs,
		disableInstantEprice,
		ePriceFontWeight,
		instantEpricePreventMultipleEpriceLeads,
		instantEpriceShowAllAfterLeadSubmit,
		restrictInsertLocationsForVehicles,
		showInstantEpriceForSharedInventory,
		allowTwoColumnCTAs,
		showSRPWindowSticker
	} = usePrefs();

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);
	const vehicleCtasLocation = getVehicleLocationName(
		'vehicle-ctas',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const componentPricing = pricingMap(pricing);
	const isEpriceDisabled = disableInstantEprice === 'true';
	const showEPrice = (id) => {
		setIsDisplayingEPrice(id === uuid && id !== 'none');
	};

	// Refreshes the mobile slide-in panel infrastructure for .dialog CTA buttons
	// on every render (no deps). Required because:
	//   1. Cards lazy-mount via IntersectionWrapper — first render of each card
	//      needs init() so cards beyond the first viewport work (DE736018).
	//   2. Mid-lifetime re-renders (lead-form submit + pricing update, ePrice
	//      toggle, pref/flag/Redux state changes) can mutate the CTA DOM and
	//      stale the panel infra; existing click handlers fire but find stale
	//      panel state and silently no-op.
	// Historically wsm-pricing-display's InstantEpriceContainer provided this
	// side effect implicitly via its mount-time useEffect, but only when ePrice
	// was eligible to render — so on disableInstantEprice='true' sites or
	// vehicles with NON_APPLICABLE/missing ePriceStatus, no per-card init ever
	// ran. slidein.init() is idempotent; extra calls on stable re-renders are
	// harmless. Do NOT add a deps array — narrowing the trigger reintroduces
	// stale-DOM bugs whose root cause is the same as DE736018.
	useEffect(() => {
		window.DDC?.mobile?.slidein?.init();
	});

	// On Deal Central SRP, do not display inventory buttons (editor-configured CTAs) but allow WIAPI CTAs to be inserted
	const inventoryButtonsToDisplay =
		dealCentralRemoveCTAs === 'true' ? [] : inventoryButtons;
	const hasInventoryButtons = Boolean(
		inventoryButtonsToDisplay && inventoryButtonsToDisplay.length > 0
	);

	const vehicleTrackingData = getVehicleObj(uuid, vehicle.title);

	const epriceCTA =
		!isEpriceDisabled &&
		!isDisplayingEPrice &&
		pricing.ePriceStatus &&
		pricing.ePriceStatus !== 'NON_APPLICABLE' ? (
			<InstantEpriceContainer
				accountId={accountId}
				buttonSize={buttonSizeEPrice}
				buttonStyle={buttonStyleEPrice}
				ePriceFontWeight={ePriceFontWeight}
				instantEpricePreventMultipleEpriceLeads={
					instantEpricePreventMultipleEpriceLeads
				}
				instantEpriceShowAllAfterLeadSubmit={
					instantEpriceShowAllAfterLeadSubmit
				}
				pageAlias={pageAlias}
				pricing={componentPricing}
				showEPrice={showEPrice}
				showInstantEpriceForSharedInventory={
					showInstantEpriceForSharedInventory
				}
				userProfile={userProfile}
				vehicleId={uuid}
				vehicleAccountId={vehicleAccountId}
				vehicleTrackingData={vehicleTrackingData}
			/>
		) : undefined;

	const windowStickerCTA = validateWindowStickerCondition(
		vehicle,
		showSRPWindowSticker,
		franchiseList,
		accounts
	) ? (
		<WindowSticker vehicle={vehicle} />
	) : undefined;

	const renderButtonList = (props) => {
		const ButtonComponent =
			allowTwoColumnCTAs === 'true' ? ButtonListTwoColumn : ButtonList;
		return <ButtonComponent {...props} />;
	};

	return (
		<>
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-listing error boundary: cta buttons error.\n${error}`
					);
					const newError = new Error(
						`ws-inv-listing error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				{targetLocations.has('vehicle-ctas')
					? renderButtonList({
							buttons: [],
							isSRP: true,
							useSRPWindowStickerPopover:
								flags['srp-window-sticker-link-change'],
							vehicleTrackingData: undefined,
							dataLocation: vehicleCtasLocation,
							afterButtons: windowStickerCTA
					  })
					: renderButtonList({
							buttons: inventoryButtonsToDisplay,
							isSRP: true,
							useSRPWindowStickerPopover:
								flags['srp-window-sticker-link-change'],
							vehicleTrackingData: hasInventoryButtons
								? vehicleTrackingData
								: undefined,
							dataLocation: vehicleCtasLocation,
							beforeButtons: epriceCTA,
							afterButtons: windowStickerCTA
					  })}
			</ErrorBoundary>
		</>
	);
};

CTAButtons.propTypes = {
	accountId: PropTypes.string,
	isDisplayingEPrice: PropTypes.bool.isRequired,
	setIsDisplayingEPrice: PropTypes.func.isRequired,
	vehicle: vehiclePropType.isRequired
};

export default CTAButtons;
